import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  computeSimulation,
  validateConstantsForSave,
} from './priceSimulateCompute';
import { roundMoney } from './budgetEngineV2';
import {
  loadInitialConstants,
  persistConstantsLocal,
  loadConstantsFromWorkspace,
  saveConstantsToWorkspace,
} from './priceSimulateStorage';
import { useTranslation } from '../../app/LanguageContext';
import { useWorkspace } from '../../app/WorkspaceContext';
import { getLanguage } from '../../app/i18n';
import {
  formatCurrency,
  formatCurrencyPlain,
  formatPercent,
  formatNumberAsCurrencyMask,
  formatIntegerPercentInputFromNumber,
  handleCurrencyInputChange,
  handleIntegerPercentInputChange,
} from '../../app/currency';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ni = (v) => {
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

const selectInputTextOnFocus = (e) => {
  const el = e.target;
  queueMicrotask(() => {
    if (el && typeof el.select === 'function') {
      el.select();
    }
  });
};

const defaultProduct = () => ({
  description: '',
  quantity: 1,
  material: 'PLA',
  grams: 0,
  print_hours: 0,
  modeling_hours: 0,
  painting_hours: 0,
  variable_costs: [],
});

const newVariableCostRow = () => ({
  id: `vc-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
  name: '',
  cost_unit: 0,
});

const makeBlankVariableCostRows = (count) => {
  const base = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const rows = [];
  for (let i = 0; i < count; i++) {
    rows.push({
      id: `vc-${base}-${i}-${Math.random().toString(36).slice(2, 11)}`,
      name: '',
      cost_unit: 0,
    });
  }
  return rows;
};

const initialDeliveryDefaultName = () => (getLanguage() === 'pt-BR' ? 'Entrega' : 'Delivery');

const stableStringifyConstants = (c) => {
  if (!c) return '';
  const mats = Object.keys(c.materials || {})
    .sort()
    .reduce((acc, k) => {
      acc[k] = c.materials[k];
      return acc;
    }, {});
  return JSON.stringify({ ...c, materials: mats });
};

const fmtMarkupRatio = (x) => {
  if (typeof x !== 'number' || !Number.isFinite(x)) return '—';
  return formatPercent(x);
};

function SummaryGroupTable({ translate, titleUpper, totalAmount, rows, formatMoney }) {
  const n = rows.length;
  if (n === 0) return null;

  const totalCellBg = (t) =>
    t.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.1)' : 'rgba(25, 118, 210, 0.08)';

  return (
    <Box>
      <TableContainer
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Table
          size="small"
          padding="checkbox"
          sx={{
            '& .MuiTableCell-root': {
              py: 0.55,
              px: 0.85,
              fontSize: '0.78rem',
              borderColor: 'divider',
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell
                colSpan={4}
                sx={{
                  py: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.55,
                    fontSize: '0.72rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                  }}
                >
                  {titleUpper}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const zebraBg = (t) =>
                i % 2 === 1
                  ? t.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.045)'
                    : 'rgba(0,0,0,0.028)'
                  : 'transparent';
              return (
                <TableRow key={row.key}>
                  <TableCell sx={{ color: 'text.primary', width: '38%', bgcolor: zebraBg }}>
                    {row.tooltip ? (
                      <Tooltip title={row.tooltip} arrow placement="top">
                        <Typography
                          component="span"
                          variant="inherit"
                          sx={{
                            fontSize: 'inherit',
                            minWidth: 0,
                            cursor: 'help',
                          }}
                          aria-label={translate('price_simulate.summary_row_info_aria')}
                        >
                          {row.label}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography component="span" variant="inherit" sx={{ fontSize: 'inherit', minWidth: 0 }}>
                        {row.label}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '20%',
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                      bgcolor: zebraBg,
                    }}
                  >
                    {row.qty}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '26%',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                      bgcolor: zebraBg,
                    }}
                  >
                    {formatMoney(row.value)}
                  </TableCell>
                  {i === 0 ? (
                    <TableCell
                      rowSpan={n}
                      align="center"
                      sx={{
                        width: '16%',
                        minWidth: '5.5rem',
                        verticalAlign: 'middle',
                        borderLeft: 1,
                        borderColor: 'divider',
                        bgcolor: totalCellBg,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.15 }}>
                        {translate('price_simulate.summary_table_total')}
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        color="primary"
                        sx={{ mt: 0.35, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {formatMoney(totalAmount)}
                      </Typography>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function PriceSummaryTables({ translate, result, payloadProduct }) {
  const q = Math.max(1, ni(result.detail?.quantity));
  const formatMoney = (v) => formatCurrency(v);

  const costRows = [];
  costRows.push({
    key: 'fil',
    label: translate('price_simulate.cost_filament'),
    qty: result.detail?.grams != null ? `${nf(result.detail.grams)} g` : '—',
    value: result.costs.filament,
    tooltip: translate('price_simulate.explain_filament'),
  });
  costRows.push({
    key: 'mach',
    label: translate('price_simulate.cost_printing_machine'),
    qty: result.detail?.print_hours != null ? `${nf(result.detail.print_hours)} h` : '—',
    value: result.costs.printing_machine,
    tooltip: translate('price_simulate.explain_print_machine'),
  });
  (result.costs.variable_costs || []).forEach((line, idx) => {
    costRows.push({
      key: `vc-${idx}`,
      label: line.name || '—',
      qty: `${q} un.`,
      value: line.cost_total,
      tooltip: translate('price_simulate.explain_variable_direct'),
    });
  });
  costRows.push({
    key: 'fail',
    label: translate('price_simulate.cost_failure'),
    qty:
      result.detail?.failure_percent != null ? `${formatPercent(result.detail.failure_percent)}%` : '—',
    value: result.costs.failure_cost,
    tooltip: translate('price_simulate.explain_failure'),
  });

  const serviceRows = [];
  serviceRows.push({
    key: 'mod',
    label: translate('price_simulate.revenue_modeling'),
    qty: `${nf(payloadProduct.modeling_hours)} h`,
    value: result.services_billing.modeling,
    tooltip: translate('price_simulate.explain_service_line'),
  });
  serviceRows.push({
    key: 'print',
    label: translate('price_simulate.summary_row_print_short'),
    qty: `${nf(payloadProduct.print_hours)} h`,
    value: result.services_billing.print_monitoring,
    tooltip: translate('price_simulate.explain_service_line'),
  });
  serviceRows.push({
    key: 'paint',
    label: translate('price_simulate.revenue_painting'),
    qty: `${nf(payloadProduct.painting_hours)} h`,
    value: result.services_billing.painting,
    tooltip: translate('price_simulate.explain_service_line'),
  });

  const servicesTotal = roundMoney(nf(result.services_billing.total));

  const marketingMoney = Math.max(
    0,
    roundMoney(nf(result.fees.price_after_marketing) - nf(result.composition.subtotal_before_marketing)),
  );

  const feeRows = [];
  feeRows.push({
    key: 'mkt',
    label: translate('price_simulate.summary_fee_marketing'),
    qty: `${formatPercent(result.fees.marketing_percent)}%`,
    value: marketingMoney,
    tooltip: translate('price_simulate.explain_marketing'),
  });
  feeRows.push({
    key: 'reinv',
    label: translate('price_simulate.summary_fee_reinvest'),
    qty: `${formatPercent(result.reinvestment.percent)}%`,
    value: result.reinvestment.amount,
    tooltip: translate('price_simulate.explain_reinvestment'),
  });
  feeRows.push({
    key: 'prof',
    label: translate('price_simulate.summary_fee_profit'),
    qty: `${formatPercent(result.profit_margin.percent)}%`,
    value: result.profit_margin.amount,
    tooltip: translate('price_simulate.explain_profit_margin'),
  });

  const feesTotal = roundMoney(
    nf(marketingMoney) + nf(result.reinvestment.amount) + nf(result.profit_margin.amount),
  );

  const costsAndServicesTotal = result.composition.subtotal_before_marketing;

  return (
    <Stack spacing={3}>
      <SummaryGroupTable
        translate={translate}
        titleUpper={translate('price_simulate.summary_section_costs_upper')}
        totalAmount={result.costs.total_direct_cost}
        rows={costRows}
        formatMoney={formatMoney}
      />
      <SummaryGroupTable
        translate={translate}
        titleUpper={translate('price_simulate.summary_section_services_upper')}
        totalAmount={servicesTotal}
        rows={serviceRows}
        formatMoney={formatMoney}
      />
      <SummaryGroupTable
        translate={translate}
        titleUpper={translate('price_simulate.summary_section_price_buildup')}
        totalAmount={feesTotal}
        rows={feeRows}
        formatMoney={formatMoney}
      />
      <Box sx={{ mt: 0.5, pt: 1.15, borderTop: 1, borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {translate('price_simulate.summary_footer_cost')}
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
              {formatMoney(costsAndServicesTotal)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {translate('price_simulate.summary_footer_price')}
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color="primary"
              sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
            >
              {formatMoney(result.pricing.final_price)}
            </Typography>
          </Box>
        </Stack>
        {ni(result.detail?.quantity) > 1 ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.65, display: 'block' }}>
            {translate('price_simulate.unit_price')}: {formatMoney(result.pricing.unit_price)}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

function buildPriceSimulationClipboardText({ translate, constants, payloadProduct, result }) {
  const pk = (k) => translate(`price_simulate.${k}`);
  const money = (v) => formatCurrencyPlain(v);
  const lines = [];

  const heading = (level, title) => {
    lines.push(`${'#'.repeat(level)} ${title}`);
  };
  const blank = () => lines.push('');
  const kv = (label, value) => lines.push(`- ${label}: ${value}`);
  const sep = () => {
    blank();
    lines.push('---');
    blank();
  };

  heading(1, pk('section_detailed'));
  blank();

  heading(2, pk('section_product'));
  if (payloadProduct.description) {
    heading(3, pk('description'));
    lines.push(payloadProduct.description);
    blank();
  }
  kv(pk('quantity'), String(payloadProduct.quantity));
  kv(pk('material'), String(payloadProduct.material));
  kv(pk('print_hours'), String(payloadProduct.print_hours));
  kv(pk('grams'), String(payloadProduct.grams));
  kv(pk('modeling_hours'), String(payloadProduct.modeling_hours));
  kv(pk('painting_hours'), String(payloadProduct.painting_hours));

  let variableCostLines = 0;
  (payloadProduct.variable_costs || []).forEach((row, idx) => {
    const hasAny = String(row.name || '').trim() !== '' || nf(row.cost_unit) > 0;
    if (!hasAny) return;
    if (variableCostLines === 0) {
      heading(3, pk('variable_costs_section'));
    }
    variableCostLines += 1;
    const nm = String(row.name || '').trim() || '—';
    kv(`${idx + 1}. ${nm}`, `${pk('cost_unit')} ${money(row.cost_unit)}`);
  });

  sep();

  heading(2, pk('section_config'));

  heading(3, pk('materials'));
  Object.keys(constants.materials || {})
    .sort()
    .forEach((name) => {
      const row = constants.materials[name];
      kv(`${name} (${pk('cost_per_kg')})`, money(nf(row?.cost_per_kg)));
    });

  heading(3, pk('machine'));
  kv(pk('equipment_maintenance_hour'), money(constants.machine?.equipment_maintenance_per_hour));
  kv(pk('equipment_degradation_hour'), money(constants.machine?.equipment_degradation_per_hour));
  kv(pk('energy_hour'), money(constants.machine?.energy_per_hour));
  kv(pk('print_labor_hour'), money(constants.machine?.print_labor_per_hour));

  heading(3, pk('services_rates'));
  kv(pk('modeling_hour'), money(constants.services?.modeling_per_hour));
  kv(pk('painting_hour'), money(constants.services?.painting_per_hour));

  heading(3, pk('percentages'));
  kv(pk('failure_pct'), `${formatIntegerPercentInputFromNumber(constants.percentages?.failure_percent)}%`);
  kv(pk('marketing_pct'), `${formatIntegerPercentInputFromNumber(constants.percentages?.marketing_percent)}%`);
  kv(pk('delivery_fee_default'), money(constants.pricing?.delivery_fee_default));
  kv(
    pk('reinvestment_pct'),
    `${formatIntegerPercentInputFromNumber(constants.pricing?.reinvestment_percent)}%`,
  );
  kv(
    pk('profit_margin_pct'),
    `${formatIntegerPercentInputFromNumber(constants.pricing?.profit_margin_percent)}%`,
  );

  sep();

  heading(2, pk('clipboard_section_breakdown'));

  heading(3, pk('section_direct_cost'));
  lines.push(`↳ ${pk('explain_filament')}`);
  kv(pk('cost_filament'), money(result.costs.filament));
  lines.push(`↳ ${pk('explain_print_machine')}`);
  kv(pk('cost_printing_machine'), money(result.costs.printing_machine));
  (result.costs.variable_costs || []).forEach((line) => {
    if (nf(line.cost_total) <= 0) return;
    kv(`${line.name} (${pk('cost_total')})`, money(line.cost_total));
  });
  kv(pk('subtotal_direct'), money(result.costs.subtotal_direct));
  lines.push(`↳ ${pk('explain_failure')}`);
  kv(pk('cost_failure'), money(result.costs.failure_cost));
  kv(pk('total_direct_cost'), money(result.costs.total_direct_cost));

  heading(3, pk('section_services_billing'));
  lines.push(`↳ ${pk('explain_service_line')}`);
  kv(pk('revenue_modeling'), money(result.services_billing.modeling));
  kv(pk('revenue_painting'), money(result.services_billing.painting));
  kv(pk('revenue_print_monitoring'), money(result.services_billing.print_monitoring));
  kv(pk('services_total'), money(result.services_billing.total));

  heading(3, pk('composition_block'));
  lines.push(`↳ ${pk('explain_composition')}`);
  kv(pk('base_direct_total'), money(result.composition.base_from_direct_cost));
  kv(pk('services_total'), money(result.services_billing.total));
  kv(pk('subtotal_before_marketing'), money(result.composition.subtotal_before_marketing));

  heading(3, pk('marketing_block'));
  lines.push(`↳ ${pk('explain_marketing')}`);
  kv(pk('marketing_pct'), `${formatPercent(result.fees.marketing_percent)}%`);
  kv(pk('price_after_marketing'), money(result.fees.price_after_marketing));

  heading(3, pk('reinvestment_block'));
  lines.push(`↳ ${pk('explain_reinvestment')}`);
  kv(pk('price_before_reinvestment'), money(result.reinvestment.price_before));
  kv(pk('reinvestment_pct_label'), `${formatPercent(result.reinvestment.percent)}%`);
  kv(pk('reinvestment_amount'), money(result.reinvestment.amount));

  heading(3, pk('profit_margin_block'));
  lines.push(`↳ ${pk('explain_profit_margin')}`);
  kv(pk('price_before_profit_margin'), money(result.profit_margin.price_before));
  kv(pk('profit_margin_pct_label'), `${formatPercent(result.profit_margin.percent)}%`);
  kv(pk('profit_margin_amount'), money(result.profit_margin.amount));

  heading(3, pk('final_price_block'));
  kv(pk('final_price'), money(result.pricing.final_price));
  kv(pk('unit_price'), money(result.pricing.unit_price));
  kv(pk('effective_markup_direct'), `${fmtMarkupRatio(result.pricing.effective_markup_on_direct)}×`);
  kv(pk('profit_on_direct'), money(result.pricing.profit_on_direct_cost));
  kv(pk('margin_on_final'), `${formatPercent(result.pricing.margin_pct_on_final)}%`);

  return lines.join('\n');
}

const CurrencyAmountField = ({
  value,
  onChange,
  label,
  placeholder,
  size = 'small',
  fullWidth,
  sx,
  disabled,
  InputLabelProps,
}) => {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState('');
  const num = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const shown = focused ? text : formatNumberAsCurrencyMask(num);

  return (
    <TextField
      size={size}
      fullWidth={fullWidth}
      label={label}
      placeholder={placeholder}
      value={shown}
      disabled={disabled}
      InputLabelProps={InputLabelProps}
      onFocus={(e) => {
        setFocused(true);
        setText(formatNumberAsCurrencyMask(num));
        selectInputTextOnFocus(e);
      }}
      onBlur={() => {
        setFocused(false);
        setText('');
      }}
      onChange={(e) => {
        handleCurrencyInputChange(e, (synth) => {
          if (typeof synth === 'string') {
            setText('');
            onChange(0);
            return;
          }
          setText(synth.target.value);
          const nv = synth.target.numericValue;
          onChange(typeof nv === 'number' && Number.isFinite(nv) ? nv : 0);
        });
      }}
      sx={sx}
    />
  );
};

const PercentAmountField = ({
  value,
  onChange,
  label,
  size = 'small',
  fullWidth,
  sx,
  inputProps,
}) => {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState('');
  const num = typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
  const shown = focused ? text : formatIntegerPercentInputFromNumber(num);

  return (
    <TextField
      size={size}
      fullWidth={fullWidth}
      label={label}
      value={shown}
      inputProps={inputProps}
      onFocus={(e) => {
        setFocused(true);
        setText(formatIntegerPercentInputFromNumber(num));
        selectInputTextOnFocus(e);
      }}
      onBlur={() => {
        const raw = text;
        setFocused(false);
        setText('');
        const digits = raw.replace(/\D/g, '');
        let n = digits === '' ? 0 : parseInt(digits, 10);
        if (!Number.isFinite(n) || n < 0) n = 0;
        onChange(n);
      }}
      onChange={(e) => {
        handleIntegerPercentInputChange(e, (synth) => {
          setText(synth.target.value);
          if (synth.target.value === '') {
            onChange(0);
            return;
          }
          if (typeof synth.numericValue === 'number' && Number.isFinite(synth.numericValue)) {
            onChange(Math.max(0, Math.round(synth.numericValue)));
          }
        });
      }}
      sx={sx}
    />
  );
};

const PriceSimulatePage = () => {
  const { translate } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const constantsRef = useRef(null);
  const [constants, setConstants] = useState(() => loadInitialConstants());
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    stableStringifyConstants(loadInitialConstants()),
  );
  const [prefsHydrated, setPrefsHydrated] = useState(false);
  const [product, setProduct] = useState(() => {
    const c = loadInitialConstants();
    const keys = Object.keys(c.materials || {});
    const fee = nf(c.pricing?.delivery_fee_default);
    const blanks = makeBlankVariableCostRows(5);
    const deliverySeed =
      fee > 0
        ? [
            {
              id: 'vc-delivery-init',
              name: initialDeliveryDefaultName(),
              cost_unit: fee,
            },
          ]
        : [];
    return {
      ...defaultProduct(),
      material: keys.includes('PLA') ? 'PLA' : keys[0] || 'PLA',
      variable_costs: [...deliverySeed, ...blanks],
    };
  });
  const [result, setResult] = useState(null);
  const [simError, setSimError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  constantsRef.current = constants;

  const isDirty = useMemo(() => {
    if (!constants) return false;
    return stableStringifyConstants(constants) !== savedSnapshot;
  }, [constants, savedSnapshot]);

  const payloadProduct = useMemo(() => {
    const rows = Array.isArray(product.variable_costs) ? product.variable_costs : [];
    return {
      quantity: product.quantity,
      material: product.material,
      description: String(product.description ?? '').trim(),
      grams: nf(product.grams),
      print_hours: nf(product.print_hours),
      modeling_hours: nf(product.modeling_hours),
      painting_hours: nf(product.painting_hours),
      variable_costs: rows.map((r) => ({
        name: String(r.name || '').trim(),
        cost_unit: nf(r.cost_unit),
      })),
    };
  }, [product]);

  const runSimulate = useCallback(() => {
    if (!constants) return;
    const r = computeSimulation(constants, payloadProduct);
    if (r.ok) {
      setResult(r.data);
      setSimError('');
    } else {
      setResult(null);
      setSimError(r.error || translate('common.error'));
    }
  }, [constants, payloadProduct, translate]);

  useEffect(() => {
    runSimulate();
  }, [runSimulate]);

  useEffect(() => {
    if (!currentWorkspace?.id) {
      setPrefsHydrated(true);
      return;
    }
    let cancelled = false;
    setPrefsHydrated(false);
    (async () => {
      try {
        const merged = await loadConstantsFromWorkspace(currentWorkspace.id);
        if (cancelled) return;
        const next = merged ?? loadInitialConstants();
        setConstants(next);
        setSavedSnapshot(stableStringifyConstants(next));
        setProduct((p) => {
          const keys = Object.keys(next.materials || {});
          const material = keys.includes(p.material) ? p.material : keys[0] || 'PLA';
          return { ...p, material };
        });
      } catch (_) {
        if (!cancelled) {
          const next = loadInitialConstants();
          setConstants(next);
          setSavedSnapshot(stableStringifyConstants(next));
        }
      } finally {
        if (!cancelled) setPrefsHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  const machineMaintenanceHour =
    constants?.machine != null ? nf(constants.machine.equipment_maintenance_per_hour) : 0;
  const machineDegradationHour =
    constants?.machine != null ? nf(constants.machine.equipment_degradation_per_hour) : 0;
  const machineEnergyHour =
    constants?.machine != null ? nf(constants.machine.energy_per_hour) : 0;
  const machineCostPerHour = constants?.machine != null
    ? roundMoney(machineMaintenanceHour + machineDegradationHour + machineEnergyHour)
    : 0;

  const companionRatePerHour =
    constants?.machine != null ? nf(constants.machine.print_labor_per_hour) : 0;

  const updateConstants = (path, value) => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const materialEntries = useMemo(() => Object.entries(constants?.materials || {}), [constants]);

  const renameMaterial = (oldKey, newKey, cost) => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      delete next.materials[oldKey];
      const name = newKey.trim() || oldKey;
      next.materials[name] = { cost_per_kg: nf(cost) };
      return next;
    });
    setProduct((p) => (p.material === oldKey ? { ...p, material: newKey.trim() || oldKey } : p));
  };

  const updateMaterialCost = (key, cost) => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      if (next.materials[key]) next.materials[key].cost_per_kg = nf(cost);
      return next;
    });
  };

  const addMaterial = () => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      let i = 1;
      let name = `Material_${i}`;
      while (next.materials[name]) {
        i++;
        name = `Material_${i}`;
      }
      next.materials[name] = { cost_per_kg: 0 };
      return next;
    });
  };

  const removeMaterial = (key) => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      delete next.materials[key];
      if (Object.keys(next.materials).length === 0) {
        next.materials.PLA = { cost_per_kg: 0 };
      }
      return next;
    });
    setProduct((p) => {
      if (p.material !== key) return p;
      const clone = { ...(constants.materials || {}) };
      delete clone[key];
      let keys = Object.keys(clone);
      if (keys.length === 0) keys = ['PLA'];
      return { ...p, material: keys[0] };
    });
  };

  const updateVariableCost = (id, field, value) => {
    setProduct((p) => {
      const rows = [...(p.variable_costs || [])];
      const i = rows.findIndex((r) => r.id === id);
      if (i < 0) return p;
      const nextRow = { ...rows[i] };
      if (field === 'name') {
        nextRow.name = value;
      } else {
        nextRow[field] = nf(value);
      }
      rows[i] = nextRow;
      return { ...p, variable_costs: rows };
    });
  };

  const addVariableCost = () => {
    setProduct((p) => ({
      ...p,
      variable_costs: [...(p.variable_costs || []), newVariableCostRow()],
    }));
  };

  const removeVariableCost = (id) => {
    setProduct((p) => ({
      ...p,
      variable_costs: (p.variable_costs || []).filter((r) => r.id !== id),
    }));
  };

  const handleSave = () => {
    if (!constants) return;
    const active = document.activeElement;
    if (active && typeof active.blur === 'function') {
      active.blur();
    }
    window.setTimeout(async () => {
      const current = constantsRef.current;
      if (!current) return;
      const err = validateConstantsForSave(current);
      if (err) {
        setSnackbar({ open: true, message: err, severity: 'error' });
        return;
      }
      try {
        persistConstantsLocal(current);
        if (currentWorkspace?.id) {
          await saveConstantsToWorkspace(currentWorkspace.id, current);
        }
        setSavedSnapshot(stableStringifyConstants(current));
        setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
      } catch (e) {
        setSnackbar({
          open: true,
          message: e.message || String(e),
          severity: 'error',
        });
      }
    }, 0);
  };

  const handleCopySummary = useCallback(async () => {
    if (!result || !constants) return;
    const text = buildPriceSimulationClipboardText({
      translate,
      constants,
      payloadProduct,
      result,
    });
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: translate('price_simulate.copy_summary_success'),
        severity: 'success',
      });
    } catch (_) {
      setSnackbar({
        open: true,
        message: translate('price_simulate.copy_summary_error'),
        severity: 'error',
      });
    }
  }, [translate, constants, payloadProduct, result]);

  if (!constants) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="error">{translate('price_simulate.load_failed')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={4}>
          <Paper elevation={2} sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {translate('price_simulate.section_config')}
            </Typography>
            {isDirty && (
              <Alert severity="warning" sx={{ py: 0.5, mb: 1 }} variant="outlined">
                {translate('price_simulate.unsaved_hint')}
              </Alert>
            )}
            <Stack spacing={1} sx={{ mb: 0.5 }}>
              {materialEntries.map(([key, row]) => (
                <Stack key={key} direction="row" alignItems="flex-end" spacing={1}>
                  <TextField
                    size="small"
                    label={translate('price_simulate.material_name')}
                    defaultValue={key}
                    key={key}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onFocus={selectInputTextOnFocus}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== key) renameMaterial(key, v, row.cost_per_kg);
                    }}
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <CurrencyAmountField
                    size="small"
                    label={translate('price_simulate.cost_per_kg')}
                    value={row.cost_per_kg}
                    onChange={(v) => updateMaterialCost(key, v)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 152, flexShrink: 0 }}
                  />
                  <IconButton
                    size="small"
                    sx={{ flexShrink: 0, mb: 0.5 }}
                    aria-label={translate('common.delete')}
                    onClick={() => removeMaterial(key)}
                    disabled={materialEntries.length <= 1}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button size="small" startIcon={<AddIcon />} onClick={addMaterial} sx={{ mt: 0.25 }}>
              {translate('price_simulate.add_material')}
            </Button>

            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.equipment_maintenance_hour')}
                value={constants.machine.equipment_maintenance_per_hour}
                onChange={(v) => updateConstants('machine.equipment_maintenance_per_hour', v)}
              />
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.equipment_degradation_hour')}
                value={constants.machine.equipment_degradation_per_hour}
                onChange={(v) => updateConstants('machine.equipment_degradation_per_hour', v)}
              />
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.energy_hour')}
                value={constants.machine.energy_per_hour}
                onChange={(v) => updateConstants('machine.energy_per_hour', v)}
              />
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.print_labor_hour')}
                value={constants.machine.print_labor_per_hour}
                onChange={(v) => updateConstants('machine.print_labor_per_hour', v)}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {translate('price_simulate.machine_cost_hour')}: {formatCurrency(machineCostPerHour)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {translate('price_simulate.companion_rate_hour')}: {formatCurrency(companionRatePerHour)}
            </Typography>

            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.modeling_hour')}
                value={constants.services.modeling_per_hour}
                onChange={(v) => updateConstants('services.modeling_per_hour', v)}
              />
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.painting_hour')}
                value={constants.services.painting_per_hour}
                onChange={(v) => updateConstants('services.painting_per_hour', v)}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              <PercentAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.failure_pct')}
                value={constants.percentages.failure_percent}
                onChange={(v) => updateConstants('percentages.failure_percent', v)}
                inputProps={{ inputMode: 'decimal' }}
              />
              <PercentAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.marketing_pct')}
                value={constants.percentages.marketing_percent}
                onChange={(v) => updateConstants('percentages.marketing_percent', v)}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              <CurrencyAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.delivery_fee_default')}
                value={constants.pricing.delivery_fee_default}
                onChange={(v) => updateConstants('pricing.delivery_fee_default', v)}
              />
              <PercentAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.reinvestment_pct')}
                value={constants.pricing.reinvestment_percent}
                onChange={(v) => updateConstants('pricing.reinvestment_percent', v)}
                inputProps={{ inputMode: 'decimal' }}
              />
              <PercentAmountField
                size="small"
                fullWidth
                label={translate('price_simulate.profit_margin_pct')}
                value={constants.pricing.profit_margin_percent}
                onChange={(v) => updateConstants('pricing.profit_margin_percent', v)}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={!isDirty || (Boolean(currentWorkspace?.id) && !prefsHydrated)}
              >
                {translate('price_simulate.save_config')}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper elevation={2} sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {translate('price_simulate.section_product')}
            </Typography>
            <Stack spacing={1}>
              <TextField
                size="small"
                fullWidth
                label={translate('price_simulate.description')}
                value={product.description ?? ''}
                onFocus={selectInputTextOnFocus}
                onChange={(e) => setProduct((p) => ({ ...p, description: e.target.value }))}
                inputProps={{ maxLength: 2000 }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                <TextField
                  size="small"
                  fullWidth
                  label={translate('price_simulate.quantity')}
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={product.quantity}
                  onFocus={selectInputTextOnFocus}
                  onChange={(e) => setProduct((p) => ({ ...p, quantity: ni(e.target.value) || 1 }))}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <InputLabel id="price-sim-material-label">{translate('price_simulate.material')}</InputLabel>
                  <Select
                    labelId="price-sim-material-label"
                    label={translate('price_simulate.material')}
                    value={product.material}
                    onChange={(e) => setProduct((p) => ({ ...p, material: e.target.value }))}
                  >
                    {Object.keys(constants.materials || {}).map((k) => (
                      <MenuItem key={k} value={k}>
                        {k}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  label={translate('price_simulate.print_hours')}
                  type="number"
                  inputProps={{ min: 0, step: 'any' }}
                  value={product.print_hours}
                  onFocus={selectInputTextOnFocus}
                  onChange={(e) => setProduct((p) => ({ ...p, print_hours: nf(e.target.value) }))}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={translate('price_simulate.grams')}
                  type="number"
                  inputProps={{ min: 0, step: 'any' }}
                  value={product.grams}
                  onFocus={selectInputTextOnFocus}
                  onChange={(e) => setProduct((p) => ({ ...p, grams: nf(e.target.value) }))}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block">
                {translate('price_simulate.print_hours_hint')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  label={translate('price_simulate.modeling_hours')}
                  type="number"
                  inputProps={{ min: 0, step: 'any' }}
                  value={product.modeling_hours}
                  onFocus={selectInputTextOnFocus}
                  onChange={(e) => setProduct((p) => ({ ...p, modeling_hours: nf(e.target.value) }))}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={translate('price_simulate.painting_hours')}
                  type="number"
                  inputProps={{ min: 0, step: 'any' }}
                  value={product.painting_hours}
                  onFocus={selectInputTextOnFocus}
                  onChange={(e) => setProduct((p) => ({ ...p, painting_hours: nf(e.target.value) }))}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Stack>

              <Divider sx={{ my: 0.5 }} />
              <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                {translate('price_simulate.variable_costs_section')}
              </Typography>
              {(product.variable_costs || []).map((row) => {
                const rowCostZero = nf(row.cost_unit) <= 0;
                const mutedOpacity = rowCostZero ? 0.5 : 1;
                const nameFieldSx = {
                  flex: { xs: '1 1 auto', sm: '2 1 0' },
                  minWidth: 0,
                  opacity: mutedOpacity,
                };
                const costFieldSx = {
                  flex: { xs: '1 1 auto', sm: '0 0 8.25rem' },
                  minWidth: 0,
                  maxWidth: { xs: 'none', sm: '9rem' },
                  opacity: mutedOpacity,
                };
                return (
                  <Stack
                    key={row.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems="flex-end"
                  >
                    <TextField
                      size="small"
                      fullWidth
                      label={translate('price_simulate.variable_cost_name')}
                      value={row.name}
                      onFocus={selectInputTextOnFocus}
                      onChange={(e) => updateVariableCost(row.id, 'name', e.target.value)}
                      sx={nameFieldSx}
                    />
                    <CurrencyAmountField
                      size="small"
                      fullWidth
                      label={translate('price_simulate.cost_unit')}
                      value={row.cost_unit}
                      onChange={(v) => updateVariableCost(row.id, 'cost_unit', v)}
                      sx={costFieldSx}
                    />
                    <IconButton
                      size="small"
                      aria-label={translate('common.delete')}
                      onClick={() => removeVariableCost(row.id)}
                      sx={{ mb: 0.5 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })}
              <Button size="small" startIcon={<AddIcon />} onClick={addVariableCost}>
                {translate('price_simulate.variable_cost_add')}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper elevation={2} sx={{ p: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                {translate('price_simulate.section_detailed')}
              </Typography>
              {result ? (
                <Tooltip title={translate('price_simulate.copy_summary_tooltip')}>
                  <IconButton
                    size="small"
                    aria-label={translate('price_simulate.copy_summary_tooltip')}
                    onClick={handleCopySummary}
                    sx={{ color: 'text.secondary', opacity: 0.7 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
            {simError && (
              <Alert severity="error" sx={{ py: 0.5, mb: 1 }} variant="outlined">
                {simError}
              </Alert>
            )}
            {result && (
              <Stack spacing={0.5}>
                {payloadProduct.description ? (
                  <>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={600}>
                        {translate('price_simulate.description')}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word', opacity: 0.92 }}>
                        {payloadProduct.description}
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 0.35 }} />
                  </>
                ) : null}

                <PriceSummaryTables
                  translate={translate}
                  result={result}
                  payloadProduct={payloadProduct}
                />
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PriceSimulatePage;
